import { aftersales } from 'types/aftersales';
import serviceTypeCodes from 'constants/serviceTypeCodes';
import { getDisplayPrice } from 'components/shared/JourneySearchTables/SearchResultsTable/SearchResultsTable.utils';
import { pricesSNSummatedFares } from './SNSummatedFares/ExchangePrice';

export const isFareDataDirect = (fare) => fare?.legs?.length === 1;
export const isFareDataSummated = (fare) =>
  fare?.legs?.length > 1 && fare?.legs?.some((l) => l?.products?.some((p) => p.code !== fare?.legs?.[0]?.products?.[0]?.code));
export const isFareDataThroughFare = (fare) =>
  fare?.legs?.length > 1 && fare?.legs?.every((l) => l?.products?.every((p) => p.code === fare?.legs?.[0]?.products?.[0]?.code));

export const retrievePricePerCarrier = (legs, carrier, usePrice = false) => {
  const carrierLegs = legs?.filter((leg) => leg.serviceType?.code === carrier)?.[0];

  if (!carrierLegs) return 0;

  const pricePerCarrier = carrierLegs?.products?.reduce(
    (previous, current) => (usePrice ? previous + current.price : previous + current.originalPrice),
    0
  );

  return pricePerCarrier;
};

export const retrieveExchangeFeePerCarrier = (legs, carrier, isOverridingRules = false) => {
  const carrierLegs = legs?.filter((leg) => leg.serviceType?.code === carrier)?.[0];

  if (!carrierLegs || isOverridingRules) return 0;

  const exchangePricePerCarrier = carrierLegs?.products?.reduce(
    (previous, current) =>
      previous + current?.afterSalesRules?.find((prev) => prev.type === aftersales.REBOOK && prev.active && prev.currentActive)?.price || 0,
    0
  );

  return exchangePricePerCarrier;
};

export const getNewFareTotal = (fare) =>
  fare?.legs?.reduce((prev, curr) => prev + curr.products?.reduce((previous, current) => previous + current.originalPrice, 0), 0);

export const priceToExchangeSummatedFareToDirect = ({
  newFaresAreCheaper,
  getESExchangeFees,
  isSameTrains,
  newFareTotal,
  originalFareTotal
}) => {
  if (newFaresAreCheaper) {
    return getESExchangeFees;
  } else {
    if (isSameTrains) {
      return Math.max(0, newFareTotal - originalFareTotal);
    }

    return Math.max(0, newFareTotal - originalFareTotal + getESExchangeFees);
  }
};

export const priceToExchangeSummatedToCheaperSummated = ({
  newESCheaper,
  newERCheaper,
  getESExchangeFees,
  getERExchangeFees,
  newERFare,
  originalERFare,
  newESFare,
  originalESFare
}) => {
  if (newESCheaper && newERCheaper) {
    return Math.max(0, getESExchangeFees + getERExchangeFees);
  }

  if (newESCheaper && !newERCheaper) {
    return Math.max(0, newERFare - originalERFare + getERExchangeFees + getESExchangeFees);
  }

  return Math.max(0, newESFare - originalESFare + getESExchangeFees + getERExchangeFees);
};

export const priceToExchangeSummatedToMoreExpensiveSummated = ({
  isSameTrains,
  newESCheaper,
  newERCheaper,
  getESExchangeFees,
  getERExchangeFees,
  newERFare,
  originalERFare,
  newESFare,
  originalESFare
}) => {
  if (!newESCheaper && !newERCheaper) {
    return isSameTrains
      ? Math.max(0, newESFare - originalESFare + newERFare - originalERFare)
      : Math.max(0, newESFare - originalESFare + getESExchangeFees + newERFare - originalERFare + getERExchangeFees);
  }

  if (!newESCheaper && newERCheaper) {
    return isSameTrains
      ? Math.max(0, newESFare - originalESFare)
      : Math.max(0, newESFare - originalESFare + getESExchangeFees + getERExchangeFees);
  }

  return isSameTrains
    ? Math.max(0, newERFare - originalERFare)
    : Math.max(0, newERFare - originalERFare + getESExchangeFees + getERExchangeFees);
};

export const priceToExchangeThroughToSummated = ({ newFaresAreCheaper, newERFare, getESExchangeFees, newFareTotal, originalFareTotal }) => {
  if (newFaresAreCheaper) {
    return Math.max(0, getESExchangeFees);
  }

  return Math.max(0, newFareTotal - originalFareTotal + getESExchangeFees);
};

export const priceToExchangeDirectToSummated = ({ newFaresAreCheaper, getESExchangeFees, newFareTotal, originalFareTotal }) => {
  if (newFaresAreCheaper) {
    return Math.max(0, getESExchangeFees);
  }

  return Math.max(0, newFareTotal - originalFareTotal + getESExchangeFees);
};

export const getDisplayPriceForSummatedFares = ({
  isGroupBooking,
  showFareDifference,
  isExchange,
  isFlowForward,
  totalPrice,
  fareData,
  originalTotalPrice,
  isExchangingMultipleLegsSummated,
  isExchangingSingleSNLegOfASummatedFare = false,
  isSNCBSummatedFare = false,
  isExchangingAThroughFare,
  isExchangingADirect,
  isSummatedFareSelectionEnabled,
  legsBeingExchanged,
  isSameTrains,
  isOverridingRules,
  shouldOverridePrice = false,
  isSNSummatedFare = false,
  oneExchangedItemHasAOPActive = false,
  isExchangingSameClassOfService = false
}) => {
  const defaultDisplayPrice = getDisplayPrice({
    isGroupBooking,
    showFareDifference,
    totalPrice,
    fareData,
    originalTotalPrice,
    isExchange,
    isFlowForward,
    shouldOverridePrice
  });

  const originalFareTotal = originalTotalPrice;
  const newFareTotal = getNewFareTotal(fareData);
  const serviceTypes = fareData.legs?.map(({ serviceType }) => serviceType?.code);

  if (isSummatedFareSelectionEnabled && !isSNSummatedFare && !isExchangingSingleSNLegOfASummatedFare && !isSNCBSummatedFare) {
    const newFaresAreCheaper = originalFareTotal >= newFareTotal;
    const fareDataIsDirect = isFareDataDirect(fareData);
    const fareDataIsSummated = isFareDataSummated(fareData);
    const fareDataIsThroughFare = isFareDataThroughFare(fareData);
    const getESExchangeFees = retrieveExchangeFeePerCarrier(legsBeingExchanged, serviceTypeCodes.EUROSTAR, isOverridingRules);
    const getERExchangeFees = retrieveExchangeFeePerCarrier(legsBeingExchanged, serviceTypeCodes.GREENSPEED, isOverridingRules);
    const newESFare = retrievePricePerCarrier(fareData?.legs, serviceTypeCodes.EUROSTAR);
    const newERFare = retrievePricePerCarrier(fareData?.legs, serviceTypeCodes.GREENSPEED);
    const originalESFare = retrievePricePerCarrier(legsBeingExchanged, serviceTypeCodes.EUROSTAR, true);
    const originalERFare = retrievePricePerCarrier(legsBeingExchanged, serviceTypeCodes.GREENSPEED, true);

    const newESCheaper = originalESFare >= newESFare;
    const newERCheaper = originalERFare >= newERFare;

    if (isExchangingADirect) {
      if (fareDataIsSummated) {
        return priceToExchangeDirectToSummated({
          newFaresAreCheaper,
          getESExchangeFees,
          newFareTotal,
          originalFareTotal
        });
      }

      return defaultDisplayPrice;
    }

    if (isExchangingAThroughFare) {
      if (fareDataIsSummated) {
        return priceToExchangeThroughToSummated({
          newFaresAreCheaper,
          newERFare,
          getESExchangeFees,
          newFareTotal,
          originalFareTotal
        });
      }
      return defaultDisplayPrice;
    }

    if (isExchangingMultipleLegsSummated) {
      if (fareDataIsDirect) {
        return priceToExchangeSummatedFareToDirect({
          newFaresAreCheaper,
          getESExchangeFees,
          isSameTrains,
          newFareTotal,
          originalFareTotal
        });
      }

      if (fareDataIsThroughFare) {
        if (newFaresAreCheaper) {
          return Math.max(0, getESExchangeFees);
        }

        return Math.max(0, newFareTotal - originalFareTotal + getESExchangeFees);
      }

      if (fareDataIsSummated) {
        if (newFaresAreCheaper) {
          return priceToExchangeSummatedToCheaperSummated({
            newESCheaper,
            newERCheaper,
            getESExchangeFees,
            getERExchangeFees,
            newERFare,
            originalERFare,
            newESFare,
            originalESFare
          });
        } else {
          return priceToExchangeSummatedToMoreExpensiveSummated({
            newESCheaper,
            newERCheaper,
            getESExchangeFees,
            getERExchangeFees,
            newERFare,
            originalERFare,
            isSameTrains,
            newESFare,
            originalESFare
          });
        }
      }

      return defaultDisplayPrice;
    }

    return defaultDisplayPrice;
  } else if (isSummatedFareSelectionEnabled && !isSNCBSummatedFare && (isSNSummatedFare || isExchangingSingleSNLegOfASummatedFare)) {
    const fareDataIsSummated = isFareDataSummated(fareData);
    const carrier = serviceTypes.includes(serviceTypeCodes.EUROSTAR) ? serviceTypeCodes.EUROSTAR : serviceTypeCodes.GREENSPEED;
    return isExchange
      ? pricesSNSummatedFares({
          legsBeingExchanged,
          fareDataIsSummated,
          isExchangingSingleSNLegOfASummatedFare,
          isExchangingMultipleLegsSummated,
          isExchangingAThroughFare,
          fareData,
          newFareTotal,
          originalFareTotal,
          isOverridingRules,
          defaultDisplayPrice,
          carrier,
          oneExchangedItemHasAOPActive,
          isExchangingSameClassOfService
        })
      : defaultDisplayPrice;
  } else {
    return defaultDisplayPrice;
  }
};
